/**
 * useAssetUpload
 *
 * Shared hook for NFT asset folder upload logic.
 * Used by both WizardStepAssets and LaunchpadDashboardPage.
 *
 * Uses presigned S3 URLs for direct browser-to-S3 uploads with high concurrency,
 * then bulk-registers metadata via the API.
 */

import { useRef, useState, useCallback } from 'react';
import {
  getPresignedAssetUrls,
  registerLaunchAssets,
  uploadToS3,
  finalizeLaunchAssets,
  clearLaunchAssets,
  type PresignedAssetUrl,
} from '../api/launches';
import { generateGlbThumbnail } from '../utils/glb-thumbnail';

export type UploadPhase =
  | 'idle'
  | 'reading'
  | 'validating'
  | 'clearing'
  | 'uploading'
  | 'registering'
  | 'finalizing'
  | 'done'
  | 'error';

export interface UploadProgress {
  phase: UploadPhase;
  statusMessage: string;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  percent: number;
}

interface AssetItem {
  tokenNumber: number;
  imageFile: File;
  contentType: string;
  metadata: { name: string; description?: string | null; attributes?: unknown; image?: string; [key: string]: unknown };
  animationFile?: File;
  animationContentType?: string;
}

interface UseAssetUploadOptions {
  launchId: string;
  /** Whether to clear existing assets before uploading */
  clearBeforeUpload?: boolean;
  onComplete?: (result: { uploaded: number; totalSupply: number }) => void;
  onError?: (error: string) => void;
}

/** Format a sorted list of token numbers into compact ranges: [1,2,3,5,7,8] -> "1-3, 5, 7-8" */
export function formatTokenList(tokens: number[]): string {
  const sorted = [...tokens].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
}

/** Map file extension to MIME content type */
const EXT_TO_CONTENT_TYPE: Record<string, string> = {
  'png': 'image/png',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'gif': 'image/gif',
  'webp': 'image/webp',
  'glb': 'model/gltf-binary',
};

export function useAssetUpload({
  launchId,
  clearBeforeUpload = false,
  onComplete,
  onError,
}: UseAssetUploadOptions) {
  const [progress, setProgress] = useState<UploadProgress>({
    phase: 'idle',
    statusMessage: '',
    totalItems: 0,
    completedItems: 0,
    failedItems: 0,
    percent: 0,
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);
  const failedItemsRef = useRef<AssetItem[]>([]);

  const updateProgress = useCallback((updates: Partial<UploadProgress>) => {
    setProgress(prev => {
      const next = { ...prev, ...updates };
      // Auto-calculate percent for upload phase
      if (next.totalItems > 0 && (next.phase === 'uploading' || next.phase === 'registering')) {
        next.percent = Math.round(((next.completedItems + next.failedItems) / next.totalItems) * 100);
      }
      return next;
    });
  }, []);

  /**
   * Read all entries from a FileSystemDirectoryReader.
   * The API may not return all entries in a single call for large directories,
   * so we must call readEntries() repeatedly until it returns an empty array.
   */
  const readAllEntries = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      const allEntries: FileSystemEntry[] = [];
      const readBatch = () => {
        reader.readEntries(
          (entries) => {
            if (entries.length === 0) {
              resolve(allEntries);
            } else {
              allEntries.push(...entries);
              readBatch();
            }
          },
          reject
        );
      };
      readBatch();
    });
  };

  /**
   * Recursively collect files from a dropped directory entry
   */
  const collectFilesFromEntry = async (entry: FileSystemEntry): Promise<File[]> => {
    const files: File[] = [];

    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      const file = await new Promise<File>((resolve) => {
        fileEntry.file((f) => {
          Object.defineProperty(f, 'webkitRelativePath', {
            value: entry.fullPath.slice(1), // Remove leading /
            writable: false,
          });
          resolve(f);
        });
      });
      files.push(file);
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const reader = dirEntry.createReader();
      const entries = await readAllEntries(reader);
      for (const childEntry of entries) {
        const childFiles = await collectFilesFromEntry(childEntry);
        files.push(...childFiles);
      }
    }

    return files;
  };

  /**
   * Parse files from a folder into paired asset items.
   * Multi-pass validation with grouped error messages.
   */
  const parseFolder = async (files: File[]): Promise<{ items: AssetItem[]; parseErrors: string[] }> => {
    const images = new Map<string, File>();
    const metadataFiles = new Map<number, File>();
    const parseErrors: string[] = [];
    const KNOWN_EXTENSIONS = new Set(Object.keys(EXT_TO_CONTENT_TYPE));

    // --- Section A: Categorize files ---
    for (const file of files) {
      const path = (file as any).webkitRelativePath || file.name;
      const pathParts = path.split('/');
      const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : path;

      if (!relativePath) continue;

      const lowerPath = relativePath.toLowerCase();

      if (lowerPath.startsWith('images/') || lowerPath.startsWith('images\\')) {
        const filename = relativePath.split(/[/\\]/).pop();
        if (filename && !filename.startsWith('.')) {
          images.set(filename, file);
        }
      } else if ((lowerPath.startsWith('metadata/') || lowerPath.startsWith('metadata\\')) && lowerPath.endsWith('.json')) {
        const filename = relativePath.split(/[/\\]/).pop();
        if (filename) {
          const tokenNumber = parseInt(filename.replace('.json', ''), 10);
          if (!isNaN(tokenNumber)) {
            metadataFiles.set(tokenNumber, file);
          }
        }
      }
    }

    // --- Section B: Detect structural problems ---
    if (metadataFiles.size === 0 && images.size === 0) {
      // Check if user uploaded flat files without folder structure
      let flatImageCount = 0;
      let flatJsonCount = 0;
      for (const file of files) {
        const path = (file as any).webkitRelativePath || file.name;
        const pathParts = path.split('/');
        const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : path;
        const ext = relativePath.split('.').pop()?.toLowerCase();
        if (ext && KNOWN_EXTENSIONS.has(ext)) flatImageCount++;
        if (ext === 'json') flatJsonCount++;
      }
      if (flatImageCount > 0 || flatJsonCount > 0) {
        parseErrors.push(
          `Found ${flatImageCount} image file(s) and ${flatJsonCount} JSON file(s), but they are not in the required folder structure. ` +
          `Your folder must contain images/ and metadata/ subdirectories. ` +
          `Example: my-collection/images/1.png, my-collection/metadata/1.json`
        );
      } else {
        parseErrors.push(
          'No images or metadata found. Upload a folder containing images/ and metadata/ subdirectories with numbered files.'
        );
      }
      return { items: [], parseErrors };
    }

    if (metadataFiles.size === 0) {
      parseErrors.push(
        `Found ${images.size} image file(s) in images/ but no metadata/ folder. ` +
        `Add a metadata/ subdirectory with numbered JSON files (e.g., metadata/1.json). ` +
        `Each JSON file needs: { "name": "NFT Name", "image": "1.png" }`
      );
      return { items: [], parseErrors };
    }

    if (images.size === 0) {
      parseErrors.push(
        `Found ${metadataFiles.size} metadata file(s) in metadata/ but no images/ folder. ` +
        `Add an images/ subdirectory with your image files (e.g., images/1.png, images/2.png).`
      );
      return { items: [], parseErrors };
    }

    // --- Section C: Read all metadata, collect per-field errors ---
    const errorGroups = {
      invalidJson: [] as number[],
      missingName: [] as number[],
      missingImage: [] as number[],
    };
    const parsedEntries: { tokenNumber: number; parsed: Record<string, unknown>; imageFilename: string }[] = [];

    for (const [tokenNumber, metaFile] of metadataFiles.entries()) {
      try {
        const text = await metaFile.text();
        const parsed = JSON.parse(text);

        if (!parsed.name || typeof parsed.name !== 'string') {
          errorGroups.missingName.push(tokenNumber);
          continue;
        }
        if (!parsed.image || typeof parsed.image !== 'string') {
          errorGroups.missingImage.push(tokenNumber);
          continue;
        }

        parsedEntries.push({ tokenNumber, parsed, imageFilename: parsed.image });
      } catch {
        errorGroups.invalidJson.push(tokenNumber);
      }
    }

    // --- Section D: Group missing image references ---
    const imageRefToTokens = new Map<string, number[]>();
    for (const entry of parsedEntries) {
      const list = imageRefToTokens.get(entry.imageFilename) || [];
      list.push(entry.tokenNumber);
      imageRefToTokens.set(entry.imageFilename, list);
    }

    const missingImageTokens = new Set<number>();
    for (const [filename, tokens] of imageRefToTokens) {
      if (!images.has(filename)) {
        if (tokens.length === 1) {
          parseErrors.push(`Token ${tokens[0]}: Image "${filename}" not found in images/ folder`);
        } else {
          parseErrors.push(`Image "${filename}" referenced by tokens ${formatTokenList(tokens)} not found in images/ folder`);
        }
        for (const t of tokens) missingImageTokens.add(t);
      }
    }

    // --- Section E: Group invalid extension errors ---
    const invalidExtTokens = new Set<number>();
    const extRefToTokens = new Map<string, number[]>();
    for (const entry of parsedEntries) {
      if (missingImageTokens.has(entry.tokenNumber)) continue;
      const ext = entry.imageFilename.split('.').pop()?.toLowerCase();
      if (!ext || !EXT_TO_CONTENT_TYPE[ext]) {
        const list = extRefToTokens.get(entry.imageFilename) || [];
        list.push(entry.tokenNumber);
        extRefToTokens.set(entry.imageFilename, list);
        invalidExtTokens.add(entry.tokenNumber);
      }
    }
    const allowedExts = Object.keys(EXT_TO_CONTENT_TYPE).join(', ');
    for (const [filename, tokens] of extRefToTokens) {
      if (tokens.length === 1) {
        parseErrors.push(`Token ${tokens[0]}: Invalid file type "${filename}". Allowed: ${allowedExts}`);
      } else {
        parseErrors.push(`"${filename}" referenced by tokens ${formatTokenList(tokens)} has invalid file type. Allowed: ${allowedExts}`);
      }
    }

    // --- Section F: Validate animation_url references ---
    const missingAnimTokens = new Set<number>();
    const animRefToTokens = new Map<string, number[]>();
    for (const entry of parsedEntries) {
      const animUrl = entry.parsed.animation_url;
      if (!animUrl || typeof animUrl !== 'string') continue;
      // External URLs are fine
      if (/^(ipfs|https?):\/\//.test(animUrl)) continue;
      // Bare filename — must exist in images/
      if (!images.has(animUrl)) {
        const list = animRefToTokens.get(animUrl) || [];
        list.push(entry.tokenNumber);
        animRefToTokens.set(animUrl, list);
        missingAnimTokens.add(entry.tokenNumber);
      }
    }
    for (const [filename, tokens] of animRefToTokens) {
      if (tokens.length === 1) {
        parseErrors.push(`Token ${tokens[0]}: animation_url "${filename}" not found in images/ folder. If this is an external URL, use https:// or ipfs:// prefix.`);
      } else {
        parseErrors.push(`animation_url "${filename}" referenced by tokens ${formatTokenList(tokens)} not found in images/ folder. If this is an external URL, use https:// or ipfs:// prefix.`);
      }
    }

    // --- Section G: Emit grouped per-field errors ---
    if (errorGroups.invalidJson.length > 0) {
      if (errorGroups.invalidJson.length === 1) {
        parseErrors.push(`Token ${errorGroups.invalidJson[0]}: Invalid JSON in metadata file`);
      } else {
        parseErrors.push(`Tokens ${formatTokenList(errorGroups.invalidJson)}: Invalid JSON in metadata files`);
      }
    }
    if (errorGroups.missingName.length > 0) {
      if (errorGroups.missingName.length === 1) {
        parseErrors.push(`Token ${errorGroups.missingName[0]}: Missing or invalid "name" field`);
      } else {
        parseErrors.push(`Tokens ${formatTokenList(errorGroups.missingName)}: Missing or invalid "name" field`);
      }
    }
    if (errorGroups.missingImage.length > 0) {
      if (errorGroups.missingImage.length === 1) {
        parseErrors.push(`Token ${errorGroups.missingImage[0]}: Missing or invalid "image" field`);
      } else {
        parseErrors.push(`Tokens ${formatTokenList(errorGroups.missingImage)}: Missing or invalid "image" field`);
      }
    }

    // --- Section H: Build items array ---
    const items: AssetItem[] = [];
    for (const entry of parsedEntries) {
      if (missingImageTokens.has(entry.tokenNumber)) continue;
      if (invalidExtTokens.has(entry.tokenNumber)) continue;
      if (missingAnimTokens.has(entry.tokenNumber)) continue;

      const ext = entry.imageFilename.split('.').pop()!.toLowerCase();
      const contentType = EXT_TO_CONTENT_TYPE[ext];

      const item: AssetItem = {
        tokenNumber: entry.tokenNumber,
        imageFile: images.get(entry.imageFilename)!,
        contentType,
        metadata: {
          name: entry.parsed.name as string,
          description: (entry.parsed.description as string) || null,
          attributes: entry.parsed.attributes || null,
          image: entry.parsed.image as string,
        },
      };

      // Preserve extra metadata fields (collection, animation_url, etc.) for original_metadata
      for (const [key, value] of Object.entries(entry.parsed)) {
        if (!['name', 'description', 'attributes', 'image'].includes(key)) {
          item.metadata[key] = value;
        }
      }

      // Attach animation file if animation_url is a bare filename in images/
      const animUrl = entry.parsed.animation_url;
      if (animUrl && typeof animUrl === 'string' && !/^(ipfs|https?):\/\//.test(animUrl)) {
        const animFile = images.get(animUrl);
        if (animFile) {
          const animExt = animUrl.split('.').pop()?.toLowerCase();
          item.animationFile = animFile;
          item.animationContentType = animExt ? (EXT_TO_CONTENT_TYPE[animExt] || 'application/octet-stream') : 'application/octet-stream';
        }
      }

      items.push(item);
    }

    return { items, parseErrors };
  };

  /**
   * Upload items using presigned S3 URLs with high concurrency
   */
  const uploadItems = async (items: AssetItem[]): Promise<{ uploaded: number; uploadErrors: string[]; failedItems: AssetItem[] }> => {
    const uploadErrors: string[] = [];
    const failed: AssetItem[] = [];
    let completed = 0;
    const CONCURRENCY = 50;
    const PRESIGN_BATCH_SIZE = 1000;

    // Build a map from tokenNumber to presigned URL info
    const urlMap = new Map<number, PresignedAssetUrl>();

    // Request presigned URLs in batches
    for (let i = 0; i < items.length; i += PRESIGN_BATCH_SIZE) {
      if (abortRef.current) break;

      const batch = items.slice(i, i + PRESIGN_BATCH_SIZE);
      const presignItems = batch.map(item => ({
        tokenNumber: item.tokenNumber,
        contentType: item.contentType,
        animationContentType: item.animationContentType,
      }));

      try {
        const { urls } = await getPresignedAssetUrls(launchId, presignItems);
        for (const url of urls) {
          urlMap.set(url.tokenNumber, url);
        }
      } catch (err) {
        // If presigning fails, mark all items in this batch as failed
        for (const item of batch) {
          failed.push(item);
          uploadErrors.push(`Token ${item.tokenNumber}: Failed to get presigned URL - ${err instanceof Error ? err.message : String(err)}`);
        }
      }
    }

    // Upload directly to S3 with high concurrency
    let idx = 0;
    const itemsToUpload = items.filter(item => urlMap.has(item.tokenNumber));
    // Track which items had thumbnails/animations successfully uploaded
    const thumbnailUploaded = new Set<number>();
    const animationUploaded = new Set<number>();

    const worker = async () => {
      while (idx < itemsToUpload.length) {
        if (abortRef.current) return;

        const currentIdx = idx++;
        const item = itemsToUpload[currentIdx];
        if (!item) break;

        const urlInfo = urlMap.get(item.tokenNumber);
        if (!urlInfo) continue;

        try {
          await uploadToS3(urlInfo.uploadUrl, item.imageFile, item.contentType);

          // For GLB files, generate and upload a thumbnail
          if (item.contentType === 'model/gltf-binary' && urlInfo.thumbnailUploadUrl) {
            try {
              const thumbBlob = await generateGlbThumbnail(item.imageFile);
              await uploadToS3(urlInfo.thumbnailUploadUrl, thumbBlob, 'image/png');
              thumbnailUploaded.add(item.tokenNumber);
            } catch (thumbErr) {
              // Thumbnail failure is non-fatal - log but continue
              console.warn(`Token ${item.tokenNumber}: Thumbnail generation failed`, thumbErr);
            }
          }

          // Upload animation file if present
          if (item.animationFile && item.animationContentType && urlInfo.animationUploadUrl) {
            await uploadToS3(urlInfo.animationUploadUrl, item.animationFile, item.animationContentType);
            animationUploaded.add(item.tokenNumber);
          }

          completed++;
        } catch (err) {
          failed.push(item);
          uploadErrors.push(`Token ${item.tokenNumber}: ${err instanceof Error ? err.message : String(err)}`);
        }

        updateProgress({
          completedItems: completed,
          failedItems: failed.length,
          statusMessage: `Uploading ${completed + failed.length} of ${items.length}${failed.length > 0 ? ` (${failed.length} failed)` : ''}`,
        });
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, itemsToUpload.length) }, () => worker());
    await Promise.all(workers);

    // Bulk register metadata for successfully uploaded items
    if (completed > 0 && !abortRef.current) {
      updateProgress({
        phase: 'registering',
        statusMessage: 'Registering metadata...',
      });

      const successfulItems = items.filter(
        item => urlMap.has(item.tokenNumber) && !failed.some(f => f.tokenNumber === item.tokenNumber)
      );

      const REGISTER_BATCH_SIZE = 500;
      for (let i = 0; i < successfulItems.length; i += REGISTER_BATCH_SIZE) {
        if (abortRef.current) break;

        const batch = successfulItems.slice(i, i + REGISTER_BATCH_SIZE);
        const registerItems = batch.map(item => {
          const urlInfo = urlMap.get(item.tokenNumber)!;
          return {
            tokenNumber: item.tokenNumber,
            name: item.metadata.name,
            description: item.metadata.description,
            attributes: item.metadata.attributes,
            imageUrl: urlInfo.cdnUrl,
            thumbnailUrl: thumbnailUploaded.has(item.tokenNumber) ? urlInfo.thumbnailCdnUrl : undefined,
            animationUrl: animationUploaded.has(item.tokenNumber) ? urlInfo.animationCdnUrl : undefined,
            originalMetadata: item.metadata,
          };
        });

        try {
          await registerLaunchAssets(launchId, registerItems);
        } catch (err) {
          // If registration fails, mark these as failed
          for (const item of batch) {
            failed.push(item);
            completed--;
          }
          uploadErrors.push(`Metadata registration failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
    }

    return { uploaded: completed, uploadErrors, failedItems: failed };
  };

  /**
   * Main upload function - processes a folder of files
   */
  const uploadFolder = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    abortRef.current = false;
    setIsUploading(true);
    setErrors([]);

    try {
      // Phase 1: Reading files
      updateProgress({
        phase: 'reading',
        statusMessage: 'Reading files...',
        totalItems: 0,
        completedItems: 0,
        failedItems: 0,
        percent: 0,
      });

      // Phase 2: Validating
      updateProgress({
        phase: 'validating',
        statusMessage: 'Validating files...',
        percent: 0,
      });

      const { items, parseErrors } = await parseFolder(fileArray);

      if (items.length === 0) {
        setErrors(parseErrors);
        onError?.(parseErrors[0] || 'No valid assets found');
        setIsUploading(false);
        updateProgress({ phase: 'error', statusMessage: 'Validation failed', percent: 0 });
        return;
      }

      // Sort by token number for orderly upload
      items.sort((a, b) => a.tokenNumber - b.tokenNumber);

      if (abortRef.current) {
        setIsUploading(false);
        updateProgress({ phase: 'idle', statusMessage: '', percent: 0 });
        return;
      }

      // Phase 3: Clear existing (if configured)
      if (clearBeforeUpload) {
        updateProgress({
          phase: 'clearing',
          statusMessage: 'Clearing existing assets...',
          percent: 0,
        });
        await clearLaunchAssets(launchId);
      }

      if (abortRef.current) {
        setIsUploading(false);
        updateProgress({ phase: 'idle', statusMessage: '', percent: 0 });
        return;
      }

      // Phase 4: Upload to S3 + register metadata
      updateProgress({
        phase: 'uploading',
        statusMessage: `Uploading 0 of ${items.length}`,
        totalItems: items.length,
        completedItems: 0,
        failedItems: 0,
        percent: 0,
      });

      const { uploaded, uploadErrors, failedItems } = await uploadItems(items);

      // Save failed items for retry
      failedItemsRef.current = failedItems;

      if (abortRef.current) {
        // Even if cancelled, finalize what was uploaded
        if (uploaded > 0) {
          try { await finalizeLaunchAssets(launchId); } catch { /* ignore */ }
        }
        setIsUploading(false);
        updateProgress({ phase: 'idle', statusMessage: '', percent: 0 });
        return;
      }

      // Phase 5: Finalize
      updateProgress({
        phase: 'finalizing',
        statusMessage: 'Finalizing...',
        percent: 100,
      });

      const result = await finalizeLaunchAssets(launchId);

      // Collect all errors
      const allErrors = [...parseErrors, ...uploadErrors];
      if (allErrors.length > 0) {
        setErrors(allErrors);
      }

      updateProgress({
        phase: 'done',
        statusMessage: failedItems.length > 0
          ? `Uploaded ${uploaded} of ${items.length} items (${failedItems.length} failed)`
          : `Uploaded ${uploaded} of ${items.length} items`,
        percent: 100,
      });

      onComplete?.({
        uploaded,
        totalSupply: result.total_supply,
      });
    } catch (err) {
      console.error('Asset upload failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to upload assets';
      onError?.(message);
      updateProgress({
        phase: 'error',
        statusMessage: message,
        percent: 0,
      });
    } finally {
      setIsUploading(false);
    }
  }, [launchId, clearBeforeUpload, onComplete, onError, updateProgress]);

  const cancelUpload = useCallback(() => {
    abortRef.current = true;
  }, []);

  /**
   * Retry only the items that failed in the previous upload
   */
  const retryFailed = useCallback(async () => {
    const items = failedItemsRef.current;
    if (items.length === 0) return;

    abortRef.current = false;
    setIsUploading(true);
    setErrors([]);
    failedItemsRef.current = [];

    try {
      updateProgress({
        phase: 'uploading',
        statusMessage: `Retrying 0 of ${items.length}`,
        totalItems: items.length,
        completedItems: 0,
        failedItems: 0,
        percent: 0,
      });

      const { uploaded, uploadErrors, failedItems } = await uploadItems(items);
      failedItemsRef.current = failedItems;

      if (abortRef.current) {
        if (uploaded > 0) {
          try { await finalizeLaunchAssets(launchId); } catch { /* ignore */ }
        }
        setIsUploading(false);
        updateProgress({ phase: 'idle', statusMessage: '', percent: 0 });
        return;
      }

      updateProgress({
        phase: 'finalizing',
        statusMessage: 'Finalizing...',
        percent: 100,
      });

      const result = await finalizeLaunchAssets(launchId);

      if (uploadErrors.length > 0) {
        setErrors(uploadErrors);
      }

      updateProgress({
        phase: 'done',
        statusMessage: failedItems.length > 0
          ? `Retried: ${uploaded} succeeded, ${failedItems.length} still failing`
          : `All ${uploaded} items uploaded successfully`,
        percent: 100,
      });

      onComplete?.({
        uploaded,
        totalSupply: result.total_supply,
      });
    } catch (err) {
      console.error('Retry failed:', err);
      const message = err instanceof Error ? err.message : 'Retry failed';
      onError?.(message);
      updateProgress({ phase: 'error', statusMessage: message, percent: 0 });
    } finally {
      setIsUploading(false);
    }
  }, [launchId, onComplete, onError, updateProgress]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry();
      if (entry) {
        const entryFiles = await collectFilesFromEntry(entry);
        files.push(...entryFiles);
      }
    }

    if (files.length > 0) {
      await uploadFolder(files);
    }
  }, [uploadFolder]);

  const dragHandlers = {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  return {
    progress,
    errors,
    isUploading,
    isDragging,
    uploadFolder,
    cancelUpload,
    retryFailed,
    failedCount: failedItemsRef.current.length,
    dragHandlers,
    inputRef,
  };
}
